import * as z from 'zod/mini';
import { METERS_PER_FOOT } from './constants.ts';

// Cable drop vocabularies. Stored as text in D1 and validated here so both
// the form and the API agree on the choices.

export const LENGTH_UNITS = ['ft', 'm'] as const;
export type LengthUnit = (typeof LENGTH_UNITS)[number];
export const lengthUnitSchema = z.enum(LENGTH_UNITS);

export const CABLE_TYPES = [
  'cat5e',
  'cat6',
  'cat6a',
  'cat7',
  'fiber_om3',
  'fiber_om4',
  'fiber_os2',
  'coax_rg6',
  'speaker_16_2',
  'speaker_14_2',
  'hdmi',
  'other',
] as const;
export type CableType = (typeof CABLE_TYPES)[number];
export const cableTypeSchema = z.enum(CABLE_TYPES);

export const CABLE_TYPE_LABELS: Readonly<Record<CableType, string>> = {
  cat5e: 'Cat5e',
  cat6: 'Cat6',
  cat6a: 'Cat6a',
  cat7: 'Cat7',
  fiber_om3: 'Fiber, multi-mode (OM3)',
  fiber_om4: 'Fiber, multi-mode (OM4)',
  fiber_os2: 'Fiber, single-mode (OS2)',
  coax_rg6: 'Coax RG6',
  speaker_16_2: 'Speaker 16/2',
  speaker_14_2: 'Speaker 14/2',
  hdmi: 'HDMI',
  other: 'Other',
};

export const TERMINATION_TYPES = [
  'rj45_keystone',
  'rj45_plug',
  'patch_panel_port',
  'f_connector',
  'lc',
  'sc',
  'banana_plug',
  'bare_wire',
  'hdmi',
  'other',
] as const;
export type TerminationType = (typeof TERMINATION_TYPES)[number];
export const terminationTypeSchema = z.enum(TERMINATION_TYPES);

export const TERMINATION_TYPE_LABELS: Readonly<Record<TerminationType, string>> = {
  rj45_keystone: 'Keystone',
  rj45_plug: 'RJ45',
  patch_panel_port: 'Patch panel port',
  f_connector: 'F connector',
  lc: 'LC',
  sc: 'SC',
  banana_plug: 'Banana plug',
  bare_wire: 'Bare wire',
  hdmi: 'HDMI',
  other: 'Other',
};

export type FiberMode = 'single_mode' | 'multi_mode';

const FIBER_MODE_BY_TYPE: Readonly<Partial<Record<CableType, FiberMode>>> = {
  fiber_os2: 'single_mode',
  fiber_om3: 'multi_mode',
  fiber_om4: 'multi_mode',
};

/** Which fiber a cable type is, or null for copper and everything else. */
export function fiberModeOf(cableType: CableType): FiberMode | null {
  return FIBER_MODE_BY_TYPE[cableType] ?? null;
}

/** A recommended maximum length: how far and in what unit. */
export interface CableLimit {
  readonly value: number;
  readonly unit: LengthUnit;
}

/**
 * The longest a run of each cable type should be (ROADMAP A13), from the
 * cabling standards: 100 m for twisted-pair copper (TIA channel limit,
 * Cat5e–Cat7), the graded distances for multi-mode fibre at 10G (OM3 300 m,
 * OM4 400 m), a generous ceiling for single-mode, and 15 m for a passive HDMI
 * run before it needs a booster. A type whose reach depends too much on gauge
 * and signal to flag (coax, speaker wire, "other") has no ceiling — null —
 * so a length is never wrongly called too long. A run longer than its type's
 * ceiling is flagged on the drop and counted on the dashboard.
 */
export const CABLE_MAX_LENGTH: Readonly<Record<CableType, CableLimit | null>> = {
  cat5e: { value: 100, unit: 'm' },
  cat6: { value: 100, unit: 'm' },
  cat6a: { value: 100, unit: 'm' },
  cat7: { value: 100, unit: 'm' },
  fiber_om3: { value: 300, unit: 'm' },
  fiber_om4: { value: 400, unit: 'm' },
  fiber_os2: { value: 10_000, unit: 'm' },
  coax_rg6: null,
  speaker_16_2: null,
  speaker_14_2: null,
  hdmi: { value: 15, unit: 'm' },
  other: null,
};

/** A length in metres, whatever unit it was entered in, so two lengths compare. */
export function lengthInMeters(value: number, unit: LengthUnit): number {
  return unit === 'm' ? value : value * METERS_PER_FOOT;
}

/**
 * Whether a run of `cableType` and this length is longer than the type's
 * recommended maximum (ROADMAP A13). A type with no ceiling, or a length not
 * recorded, is never over: there is nothing to measure it against.
 */
export function isOverLength(
  cableType: CableType,
  lengthValue: number | null,
  lengthUnit: LengthUnit,
): boolean {
  const max = CABLE_MAX_LENGTH[cableType];
  if (max === null || lengthValue === null) {
    return false;
  }
  return lengthInMeters(lengthValue, lengthUnit) > lengthInMeters(max.value, max.unit);
}

/** "100 m" — a cable type's recommended maximum length, or '' when it has none. */
export function maxLengthText(cableType: CableType): string {
  const max = CABLE_MAX_LENGTH[cableType];
  return max === null ? '' : `${String(max.value)} ${max.unit}`;
}

/** Colours a run may be given; none means the map's default blue. */
export const DROP_COLORS = [
  'blue',
  'green',
  'orange',
  'red',
  'purple',
  'yellow',
  'pink',
  'gray',
] as const;
export type DropColor = (typeof DROP_COLORS)[number];
export const dropColorSchema = z.enum(DROP_COLORS);

export const DROP_COLOR_LABELS: Readonly<Record<DropColor, string>> = {
  blue: 'Blue',
  green: 'Green',
  orange: 'Orange',
  red: 'Red',
  purple: 'Purple',
  yellow: 'Yellow',
  pink: 'Pink',
  gray: 'Grey',
};

/** The stroke the map draws a run in. */
export const DROP_COLOR_HEX: Readonly<Record<DropColor, string>> = {
  blue: '#2457d6',
  green: '#16a34a',
  orange: '#ea580c',
  red: '#dc2626',
  purple: '#7c3aed',
  yellow: '#ca8a04',
  pink: '#db2777',
  gray: '#6b7280',
};

/** Active runs carry links; a spare is pulled and labelled but links nothing. */
export const DROP_STATUSES = ['active', 'spare'] as const;
export type DropStatus = (typeof DROP_STATUSES)[number];
export const dropStatusSchema = z.enum(DROP_STATUSES);

export const DROP_STATUS_LABELS: Readonly<Record<DropStatus, string>> = {
  active: 'Active',
  spare: 'Spare',
};

/** What a cable tester reported for a run; '' while no result has been recorded. */
export const TEST_RESULTS = ['', 'pass', 'fail', 'marginal'] as const;
export type TestResult = (typeof TEST_RESULTS)[number];
export const testResultSchema = z.enum(TEST_RESULTS);

export const TEST_RESULT_LABELS: Readonly<Record<TestResult, string>> = {
  '': 'Not recorded',
  pass: 'Pass',
  fail: 'Fail',
  marginal: 'Marginal',
};

/** The date a run was tested: an ISO calendar day, or null while it never was. */
export const testedOnSchema = z.nullable(z.iso.date());

/** The facts of a run's test, as a page or a report states them. */
export interface CableTest {
  readonly testedOn: string | null;
  readonly testResult: TestResult;
  readonly measuredLength: number | null;
  readonly lengthUnit: LengthUnit;
}

/** "Tested 2026-09-01 · Pass · measured 42 ft", leaving out what was not recorded; '' when nothing was. */
export function describeCableTest(test: CableTest): string {
  return [
    test.testedOn === null ? '' : `Tested ${test.testedOn}`,
    test.testResult === '' ? '' : TEST_RESULT_LABELS[test.testResult],
    test.measuredLength === null
      ? ''
      : `measured ${String(test.measuredLength)} ${test.lengthUnit}`,
  ]
    .filter((part) => part !== '')
    .join(' · ');
}
